// Битмап-шрифт этикетки: чтение сгенерированных глифов, метрики, отрисовка (T-028).
//
// Глифы приезжают из `font.generated.ts` — их собирает `scripts/build-label-font.mjs`
// из IBM Plex, того же шрифта, что на экране (`DESIGN.md`). Рантайм шрифты не парсит:
// печать обязана начинаться сразу и офлайн, а не ждать загрузку и разбор ttf.

import { clearPixel, setPixel, type MutableBitmap } from './bitmap';
import {
  FONT_DATA_BASE64,
  FONT_SIZES,
  GLYPH_TABLE_BASE64,
  type FontSizeId,
} from './font.generated';

export type { FontSizeId } from './font.generated';

export type GlyphMeta = {
  readonly size: FontSizeId;
  readonly code: number;
  readonly width: number;
  readonly height: number;
  /** Смещение от точки вставки влево (у части глифов отрицательное). */
  readonly left: number;
  /** Смещение от базовой линии вверх, в растровых координатах (вниз положительно). */
  readonly top: number;
  /** На сколько сдвинуться вправо после глифа. */
  readonly advance: number;
  readonly offset: number;
};

/**
 * Как рисовать строку.
 * - `tracking` — разрядка: точки между буквами (капитель макета — 0,12–0,22 em);
 * - `invert` — белым по чёрному: строка гасит точки, а не зажигает (полоса ярлыка);
 * - `embolden` — дожирнение: каждый штрих шире на столько точек вправо. Жирнее Bold
 *   начертаний у шрифта нет, а белая буква на чёрной полосе на термобумаге
 *   зарастает по краям — тонкий штрих пропадает первым.
 */
export type TextStyle = {
  readonly tracking?: number;
  readonly invert?: boolean;
  readonly embolden?: number;
};

function decodeBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

const DATA = decodeBase64(FONT_DATA_BASE64);

// Индекс строится один раз: линейный поиск по полутора тысячам глифов на КАЖДЫЙ
// символ строки превратил бы отрисовку названия в сотни тысяч сравнений.
// Ключ — пара (кегль, код).
const INDEX = new Map<string, GlyphMeta>();
{
  const raw = decodeBase64(GLYPH_TABLE_BASE64);
  const view = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
  const FIELDS = 8;
  for (let at = 0; at + FIELDS * 4 <= raw.byteLength; at += FIELDS * 4) {
    const read = (i: number): number => view.getInt32(at + i * 4, true);
    const size = FONT_SIZES[read(0)]?.id;
    if (!size) continue;
    const glyph: GlyphMeta = {
      size,
      code: read(1),
      width: read(2),
      height: read(3),
      left: read(4),
      top: read(5),
      advance: read(6),
      offset: read(7),
    };
    INDEX.set(`${size}:${glyph.code}`, glyph);
  }
}

/** Кегль в точках — нужен раскладке, чтобы считать строки. */
export function sizeHeight(size: FontSizeId): number {
  return FONT_SIZES.find((entry) => entry.id === size)?.px ?? 0;
}

/**
 * Глиф или `null`, если символа нет в наборе.
 *
 * `null`, а не подстановка «?»: беззвучная замена символа означала бы этикетку
 * с искажённым названием продукта, и повар не отличил бы её от правильной.
 * Что делать с пропуском, решает вызывающий — см. `drawText`.
 */
export function glyphFor(size: FontSizeId, codePoint: number): GlyphMeta | null {
  return INDEX.get(`${size}:${codePoint}`) ?? null;
}

/** Ширина строки в точках без отрисовки — раскладке нужно мерить до рисования. */
export function measureText(size: FontSizeId, text: string, style: TextStyle = {}): number {
  const tracking = style.tracking ?? 0;
  let width = 0;
  let count = 0;
  for (const char of text) {
    const glyph = glyphFor(size, char.codePointAt(0) ?? 0);
    width += glyph?.advance ?? 0;
    count += 1;
  }
  // Разрядка — между буквами, не после последней: иначе строка «уезжала» бы от
  // правого края на ширину разрядки. Дожирнение выносит последний штрих вправо.
  return width + Math.max(0, count - 1) * tracking + (count > 0 ? (style.embolden ?? 0) : 0);
}

/**
 * Отрисовать строку. `y` — БАЗОВАЯ ЛИНИЯ, а не верх строки.
 *
 * Базовая линия, потому что по ней выравниваются глифы разной высоты: «Ё» выше
 * «о», и выравнивание по верху развалило бы строку. Возвращает достигнутую
 * координату X, чтобы вызывающий мог продолжить строку другим кеглем.
 */
export function drawText(
  bitmap: MutableBitmap,
  size: FontSizeId,
  text: string,
  x: number,
  baselineY: number,
  style: TextStyle = {},
): number {
  const tracking = style.tracking ?? 0;
  const extra = style.embolden ?? 0;
  const paint = style.invert ? clearPixel : setPixel;
  let cursor = x;
  let first = true;
  for (const char of text) {
    if (!first) cursor += tracking;
    first = false;
    const glyph = glyphFor(size, char.codePointAt(0) ?? 0);
    if (!glyph) {
      // Неизвестный символ пропускается вместе с его шириной: пустое место
      // честнее, чем чужая буква на месте нужной.
      continue;
    }
    const stride = Math.ceil(glyph.width / 8);
    for (let row = 0; row < glyph.height; row += 1) {
      for (let column = 0; column < glyph.width; column += 1) {
        const byte = DATA[glyph.offset + row * stride + (column >> 3)] ?? 0;
        if ((byte & (0b1000_0000 >> (column & 7))) !== 0) {
          const x0 = cursor + glyph.left + column;
          for (let x = x0; x <= x0 + extra; x += 1) {
            paint(bitmap, x, baselineY + glyph.top + row);
          }
        }
      }
    }
    cursor += glyph.advance;
  }
  return cursor;
}

/** Знаков без пробелов — мера того, сколько текста не попало на бумагу. */
function letters(text: string): number {
  return [...text.replace(/\s+/gu, '')].length;
}

/** Обрезанная строка и сколько знаков (без пробелов) ушло за «…». */
type Clipped = { readonly text: string; readonly hidden: number };

function clip(size: FontSizeId, text: string, maxWidth: number): Clipped {
  if (measureText(size, text) <= maxWidth) {
    return { text, hidden: 0 };
  }
  const ellipsis = '…';
  // Запасной знак — на случай кегля без «…»: ТРИ точки, а не одна. Одна точка
  // читалась как сокращение: «в закр.» — это «в закрытой», а не обрезанная строка.
  const mark = glyphFor(size, ellipsis.codePointAt(0) ?? 0) ? ellipsis : '...';
  const markWidth = measureText(size, mark);
  const characters = [...text];
  let width = 0;
  let taken = 0;
  while (taken < characters.length) {
    const next = measureText(size, characters[taken] ?? '');
    if (width + next + markWidth > maxWidth) {
      break;
    }
    width += next;
    taken += 1;
  }
  return {
    text: characters.slice(0, taken).join('').trimEnd() + mark,
    hidden: Math.max(1, letters(characters.slice(taken).join(''))),
  };
}

/**
 * Обрезать строку по ширине, добавив многоточие.
 *
 * Возвращает исходную строку, если она влезает. Многоточие ОДНИМ символом «…»,
 * а не тремя точками: три точки съедают на треть больше места ровно там, где
 * места и не хватило.
 */
export function truncateToWidth(size: FontSizeId, text: string, maxWidth: number): string {
  return clip(size, text, maxWidth).text;
}

/** Текст, разложенный по строкам, и сколько знаков (без пробелов) не влезло. */
export type FittedText = {
  readonly lines: readonly string[];
  /** 0 — текст на бумаге целиком; больше — столько знаков ушло за «…». */
  readonly hidden: number;
};

type Piece = { readonly text: string; readonly glue: string };

/** Слова текста; с `hyphens` — ещё и части слов после дефиса («супа-» + «лапши»). */
function piecesOf(text: string, hyphens: boolean): Piece[] {
  const pieces: Piece[] = [];
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const parts = hyphens ? word.split(/(?<=[^-]-)(?=[^-])/u) : [word];
    parts.forEach((part, i) => {
      pieces.push({ text: part, glue: i === 0 ? ' ' : '' });
    });
  }
  return pieces;
}

function fillLines(
  size: FontSizeId,
  pieces: readonly Piece[],
  widthOf: (line: number) => number,
  maxLines: number,
): FittedText {
  const join = (from: number): string =>
    pieces
      .slice(from)
      .map((piece, i) => (i === 0 ? piece.text : `${piece.glue}${piece.text}`))
      .join('');
  const lines: string[] = [];
  let hidden = 0;
  let at = 0;
  while (at < pieces.length && lines.length < maxLines) {
    const width = widthOf(lines.length);
    if (lines.length === maxLines - 1) {
      const last = clip(size, join(at), width);
      lines.push(last.text);
      hidden += last.hidden;
      break;
    }
    let current = pieces[at]?.text ?? '';
    at += 1;
    while (at < pieces.length) {
      const next = pieces[at];
      const candidate = `${current}${next?.glue ?? ''}${next?.text ?? ''}`;
      if (measureText(size, candidate) > width) break;
      current = candidate;
      at += 1;
    }
    // Одно слово шире поля — рубится с «…» на своей строке.
    const line = clip(size, current, width);
    lines.push(line.text);
    hidden += line.hidden;
  }
  return { lines, hidden };
}

/**
 * Разложить текст не больше чем на `maxLines` строк — как `line-clamp` браузера,
 * в котором собран макет ярлыка.
 *
 * `widths` — ширина каждой строки; последняя повторяется для остальных (у условий
 * хранения первая строка короче: перед ней «ХРАНИТЬ»).
 *
 * Перенос ПО СЛОВАМ: разрыв посреди слова читается как другой продукт, а повар
 * сверяет наклейку с контейнером взглядом. Сначала — только по пробелам («для» /
 * «супа-лапши»); после дефиса («супа-» / «лапши») — лишь если иначе текст не влез.
 * Последняя строка получает ВЕСЬ остаток текста и обрезается по ширине с «…»:
 * «супа-лапши, охлажд…» сообщает больше, чем «супа-лапши,…» с выпавшим словом.
 * Обрезка ВИДНА — честнее молчаливой потери хвоста; а `hidden` подсказывает
 * раскладке, что стоит попробовать кегль мельче.
 */
export function fitText(
  size: FontSizeId,
  text: string,
  widths: number | readonly number[],
  maxLines: number,
): FittedText {
  if (maxLines < 1) {
    return { lines: [], hidden: letters(text) };
  }
  const widthOf = (line: number): number =>
    typeof widths === 'number' ? widths : (widths[Math.min(line, widths.length - 1)] ?? 0);
  const whole = fillLines(size, piecesOf(text, false), widthOf, maxLines);
  if (whole.hidden === 0 || !text.includes('-')) {
    return whole;
  }
  return fillLines(size, piecesOf(text, true), widthOf, maxLines);
}

/** Строки `fitText` — когда важны только они. */
export function wrapText(
  size: FontSizeId,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  return [...fitText(size, text, maxWidth, maxLines).lines];
}
